import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { fechaHora, poli, soloFecha } from './poliApi';
import DetallePrueba from './DetallePrueba';

/**
 * P-9 Panel ejecutivo de Gerencia: cuantas pruebas hay en cada estado y lo que necesita atencion hoy
 * (inasistencias del dia, plazos de reagendar por vencer, informes atrasados, observaciones sin atender).
 */
const Cifra = ({ titulo, valor, nota, alerta }) => (
    <div className="col-6 md:col-3">
        <div className="card mb-0 h-full" style={alerta && valor > 0 ? { borderLeft: '4px solid #d9534f' } : undefined}>
            <div className="text-600 text-sm">{titulo}</div>
            <div className="text-3xl font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</div>
            {nota && <div className="text-500 text-xs mt-1">{nota}</div>}
        </div>
    </div>
);

const diasPara = (fecha) => {
    if (!fecha) return '';
    const d = Math.round((new Date(`${fecha}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);
    return d < 0 ? `vencido hace ${-d} d` : d === 0 ? 'vence hoy' : `quedan ${d} d`;
};

const PoliPanel = () => {
    const toast = useRef(null);
    const [cfg, setCfg] = useState(null);
    const [p, setP] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [detalle, setDetalle] = useState(null);

    const cargar = useCallback(async () => {
        setCargando(true);
        try {
            const [c, d] = await Promise.all([poli('/config'), poli('/panel')]);
            setCfg(c);
            setP(d);
        } catch (e) {
            toast.current && toast.current.show({ severity: 'error', summary: 'Error', detail: e.message, life: 4000 });
        } finally {
            setCargando(false);
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const persona = (r) => <div><b>{r.apellidos} {r.nombres}</b><br /><small className="text-600">{r.codigo} · {r.compania}</small></div>;
    const tabla = (titulo, filas, columnas, vacio) => (
        <div className="col-12 lg:col-6">
            <div className="card mb-0 h-full">
                <h5>{titulo} <span className="text-600">({filas.length})</span></h5>
                <DataTable value={filas} size="small" rowHover dataKey="prueba_id" onRowClick={(e) => setDetalle(e.data.prueba_id)}
                    style={{ cursor: 'pointer' }} emptyMessage={vacio} responsiveLayout="scroll">
                    <Column header="Colaborador" body={persona} />
                    {columnas}
                </DataTable>
            </div>
        </div>
    );

    const m = (p && p.delMes) || {};
    const total = p ? p.porEstado.reduce((s, e) => s + Number(e.total), 0) : 0;

    return (
        <div className="grid">
            <Toast ref={toast} />
            <div className="col-12 flex align-items-center justify-content-between flex-wrap gap-2">
                <h4 className="m-0">Panel ejecutivo de poligrafia</h4>
                <Button label="Actualizar" icon="pi pi-refresh" className="p-button-outlined" onClick={cargar} loading={cargando} />
            </div>
            {p && (
                <>
                    <Cifra titulo="Solicitadas este mes" valor={m.solicitadas} />
                    <Cifra titulo="Realizadas este mes" valor={m.realizadas} nota="con asistencia confirmada" />
                    <Cifra titulo="No asistieron este mes" valor={m.no_asistieron} />
                    <Cifra titulo="Observaciones sin atender" valor={p.observacionesPendientes} nota={`informes devueltos al poligrafista · ${p.observacionesFinanciero || 0} a Financiero sin revisar`} alerta />

                    <div className="col-12">
                        <div className="card mb-0">
                            <h5>Pruebas por estado <span className="text-600">({total})</span></h5>
                            <div className="flex flex-wrap gap-2">
                                {p.porEstado.map((e) => (
                                    <div key={e.estado} className="flex align-items-center gap-2 border-1 surface-border border-round px-3 py-2"
                                        style={{ minWidth: '11rem' }}>
                                        <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: '50%', background: e.color, flex: 'none' }} />
                                        <span className="text-xl font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{e.total}</span>
                                        <span className="text-sm">{e.nombre}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {tabla('Hoy no asistieron', p.inasistenciasHoy, [
                        <Column key="c" header="Cita" body={(r) => fechaHora(r.fecha_asignada)} />,
                        <Column key="p" header="Reagendar hasta" body={(r) => soloFecha(`${r.plazo_reagendar}T00:00:00`)} />
                    ], 'Nadie falto hoy')}
                    {tabla('Plazo de reagendar por vencer (3 dias)', p.plazosPorVencer, [
                        <Column key="p" header="Plazo" body={(r) => <div>{soloFecha(`${r.plazo_reagendar}T00:00:00`)}<br /><small className="text-600">{diasPara(r.plazo_reagendar)}</small></div>} />
                    ], 'Ningun plazo por vencer')}
                    {tabla('Informes atrasados (mas de 2 dias sin registrar)', p.informesAtrasados, [
                        <Column key="a" header="Asistio" body={(r) => fechaHora(r.asistencia_fecha)} />,
                        <Column key="g" header="Poligrafista" field="poligrafista" />
                    ], 'Ningun informe atrasado')}
                </>
            )}
            <DetallePrueba pruebaId={detalle} cfg={cfg} alCerrar={() => setDetalle(null)} alCambiar={() => cargar()} />
        </div>
    );
};

export default PoliPanel;
